import styled from 'styled-components';
import { key } from 'styled-theme';

// Main Container Styled Components
export const PageContainer = styled.div`
  min-height: 100vh;
  margin-top: 95px;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

export const Header = styled.header`
  background: linear-gradient(135deg, #94c5a0 0%, #5ba6ce 100%);
  padding: 1rem 2rem;
  color: white;
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
`;

export const LogoIcon = styled.div`
  width: 40px;
  height: 24px;
  background: linear-gradient(45deg, #94c5a0, #5ba6ce, #4a90e2);
  border-radius: 4px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #5ba6ce, #4a90e2);
    border-radius: 0 0 4px 4px;
  }
`;

export const ContentContainer = styled.div`
  padding: 2rem 0;
  width: 84%;

  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// Card Styled Components
export const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

export const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.clickable ? '#edf2f7' : '#f7fafc')};
  }
`;

export const CardTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CardContent = styled.div`
  padding: 1.5rem;
`;

// Detail Components
export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const DetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const DetailValue = styled.span`
  font-size: 0.875rem;
  color: #1a202c;
  font-weight: 500;
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

// Contact Components
export const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #edf2f7;
  }
`;

export const ContactIcon = styled.div`
  color: #4299e1;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ContactText = styled.span`
  color: #1a202c;
  font-size: 0.875rem;
  font-weight: 500;
`;

export const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// Button Component
export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background-color: #4299e1;
    color: white;
    
    &:hover {
      background-color: #3182ce;
    }
  `
      : `
    background-color: #edf2f7;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background-color: #e2e8f0;
    }
  `}
`;

// Badge Component
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${(props) =>
    props.variant === 'status'
      ? `
    background-color: #c6f6d5;
    color: #22543d;
  `
      : `
    background-color: #fef5e7;
    color: #744210;
    margin: 0.25rem;
  `}
`;

// Map Component
export const MapContainer = styled.div`
  height: 300px;
  background-color: #f0f8ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  background: linear-gradient(45deg, #f0f8ff 25%, transparent 25%),
    linear-gradient(-45deg, #f0f8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f8ff 75%),
    linear-gradient(-45deg, transparent 75%, #f0f8ff 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
`;

export const MapIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #94c5a0, #5ba6ce);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
`;

export const MapText = styled.p`
  color: #4a5568;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

export const MapSubText = styled.small`
  font-size: 0.875rem;
  opacity: 0.8;
`;

// Report Components
export const ReportItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

export const ReportInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ReportTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
`;

export const ReportStatus = styled.span`
  font-size: 0.75rem;
  color: #718096;
`;

export const ReportActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ReportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const OverlayContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const ActionButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

// Application Header Components
export const ApplicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

export const ApplicationTitle = styled.h1`
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
`;

export const ApplicationId = styled.p`
  color: #718096;
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
`;

export const OverlayDescription = styled.p`
  color: #718096;
  font-size: 0.875rem;
  margin-top: 1rem;
  margin-bottom: 0;
`;

export const DevelopmentDescription = styled.div`
  margin-top: 1rem;
`;
